import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from '~/utils/navigation';
import { signout } from '~/components/authentication/ducks/index.js';
import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import { selectPlayerId } from '../ducks/index.js';
import ConfirmMessage, { IconIdentifier } from '~/components/action-screen-confirm-message.js';

import { selectSiteType, selectLicense } from '~/components/brand/ducks/selectors.js';
import {
	LICENSE_DGA,
	LICENSE_SGA,
	LICENSE_MGA,
	LICENSE_PLGA,
	LICENSE_OGA,
	LICENSE_KSA
} from '~/utils/license-utils.js';
import { useCloseCurrentContext } from '~/hooks/use-close-context.js';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer.js';
import { start, durationUpdate, realityCheckTimeReachedToggle, timeLimitExceededLog } from './ducks/index.js';

import { selectIsInGame } from './ducks/selectors.js';
import {
	getTexts,
	getTimestamp,
	getPopupInfo,
	setTimestamp,
	setAcknowledgementTimestamp
} from './reality-check-utils.js';
import RealityCheckView from './reality-check-view.js';
import { APP_EVENTS, registerAppEventCallback, sendAppBridgeEvent, WEB_EVENTS } from '~/components/app-bridge/index.js';
import { ROUTE_HOME } from '~/components/router/constants';
import shouldRedirectAfterLogout from '../should-redirect-after-logout.js';
import BinaryQuestion from '~/components/binary-question';
import useTranslations from '~/components/translation/use-translations';

const textKeys = [
	'API_ERROR_TITLE',
	'API_ERROR_DESCRIPTION',
	'BUTTON_OK',
	'MOBILE_REALITY_CHECK_WAGERED_HEADER',
	'MOBILE_REALITY_CHECK_RG_MESSAGE'
];

/**
 * Reality Check Logic for MGA(No account sites), MGA, DGA, Pzbuk and SGA sites
 * The button logic works based on the licenses.
 */
const RealityCheck = () => {
	// Component states
	const [show, setShow] = useState(false);
	const [texts, setTexts] = useState(getTexts({ texts: {} }));
	const [confirm, setConfirm] = useState({
		show: false,
		iconIdentifier: IconIdentifier.DEFAULT_SUCCESS,
		title: '',
		message: '',
		buttonText: '',
		buttonCallback: undefined,
		closeButtonText: '',
		closeCallback: undefined,
		buttons: []
	});
	const [apiError, setApiError] = useState(false);

	// Redux Selectors
	const isInGame = useSelector(selectIsInGame);
	const authenticated = useSelector(authenticationSelector);
	const license = useSelector(selectLicense);
	const siteType = useSelector(selectSiteType);
	const playerId = useSelector(selectPlayerId);

	const { translations, isLoadingTranslations } = useTranslations(textKeys);

	// Other hooks
	const dispatch = useDispatch();
	const router = useRouter();
	const closeCurrentContext = useCloseCurrentContext();
	const transformPath = useContextualPathTransformer();

	const popUpTexts = useMemo(() => {
		if (!isLoadingTranslations) {
			return {
				MOBILE_REALITY_CHECK_RG_MESSAGE: translations.MOBILE_REALITY_CHECK_RG_MESSAGE,
				MOBILE_REALITY_CHECK_WAGERED_HEADER: translations.MOBILE_REALITY_CHECK_WAGERED_HEADER
			};
		}
	}, [
		isLoadingTranslations,
		translations.MOBILE_REALITY_CHECK_RG_MESSAGE,
		translations.MOBILE_REALITY_CHECK_WAGERED_HEADER
	]);

	const startRealityCheck = useCallback(
		async timestamp => {
			// taking durationReached as an argument to avoid closure issues with useEffect and callback being passed to redux
			const showRealityCheck = async durationReached => {
				if (authenticated && durationReached > 0) {
					try {
						const response = await getPopupInfo();
						if (response.status === 'SUCCESS') {
							if (response.result.playTimeMinutes > 0 || response.result.playTimeHours > 0) {
								setShow(true);
								setTexts({
									...getTexts(response.result),
									MOBILE_REALITY_CHECK_RG_MESSAGE: popUpTexts.MOBILE_REALITY_CHECK_RG_MESSAGE,
									MOBILE_REALITY_CHECK_WAGERED_HEADER: popUpTexts.MOBILE_REALITY_CHECK_WAGERED_HEADER
								});
								timeLimitExceededLog({
									playerId,
									limitLeftInHour: 0,
									limitAmountInHour: durationReached,
									rgType: 'REALITY'
								});
								await setAcknowledgementTimestamp(false);
							} else {
								startRealityCheck();
							}
						} else {
							setApiError(true);
						}
					} catch (e) {
						// TODO error log on new relic maybe?
						setApiError(true);
					}
				}
			};

			try {
				if (timestamp === undefined || Number.isNaN(timestamp)) {
					const response = await setTimestamp();
					if (response.status === 'SUCCESS') {
						if (response.result?.started) {
							dispatch(start(getTimestamp(), showRealityCheck));
						}
					} else {
						setApiError(true);
					}
				} else {
					dispatch(start(timestamp, showRealityCheck));
				}
			} catch (e) {
				// TODO error log on new relic maybe?
				setApiError(true);
			}
		},
		[dispatch, authenticated, playerId, popUpTexts]
	);

	const startRealityCheckWithTimeStamp = useCallback(() => {
		startRealityCheck(getTimestamp());
	}, [startRealityCheck]);

	useEffect(() => {
		// TODO: need to refactor this, this effect needs to be run only when startRealityCheck callback has all the data it needs
		// dont run the function until we have everything we need in startRealityCheck callback
		if (!authenticated || !playerId || !popUpTexts) {
			return;
		}
		startRealityCheckWithTimeStamp();
	}, [authenticated, playerId, popUpTexts, startRealityCheckWithTimeStamp]);

	// MOBILE APP RELATED EVENT
	useEffect(() => {
		registerAppEventCallback(APP_EVENTS.START_REALITY_CHECK, startRealityCheckWithTimeStamp);
	}, [startRealityCheckWithTimeStamp]);

	// Making sure its an AccountSiteType
	const isRegularSite = siteType && siteType.toUpperCase() === 'REGULAR';

	const onFetchFail = () => {
		setShow(false);
	};

	const onContinue = useCallback(async () => {
		try {
			const response = await setTimestamp();
			if (response.result !== undefined) {
				const { title, message, button: text } = response.result;
				setShow(false);
				await setAcknowledgementTimestamp(true);
				// TODO: Buttons can be moved out of confirm state later
				setConfirm({
					show: true,
					iconIdentifier: IconIdentifier.DEFAULT_SUCCESS,
					title,
					message,
					buttons: [
						{
							class: '',
							name: 'close',
							onclick: () => {
								startRealityCheck(getTimestamp());
								setConfirm(confirmData => ({
									...confirmData,
									show: false
								}));
								dispatch(realityCheckTimeReachedToggle(false));
							},
							text
						}
					]
				});
			} else {
				setApiError(true);
				onFetchFail();
			}
		} catch (e) {
			// TODO error log on new relic maybe?
			setApiError(true);
		}
	}, [dispatch, startRealityCheck]);

	const reloadPage = () => {
		// page reload here
		window.location.reload();
	};

	const onDisable = async () => {
		dispatch(
			durationUpdate(
				-1,
				result => {
					setShow(false);
					setAcknowledgementTimestamp(false, true);
					setConfirm({
						iconIdentifier: IconIdentifier.DEFAULT_SUCCESS,
						show: true,
						title: result.title,
						message: result.message,
						buttons: [
							{
								class: '',
								name: 'close',
								onclick: () => {
									setConfirm(confirmData => ({
										...confirmData,
										show: false
									}));
								},
								text: result.button
							}
						]
					});
				},
				onFetchFail
			)
		);

		dispatch(realityCheckTimeReachedToggle(false));
	};

	const onLogout = async () => {
		setShow(false);
		await setAcknowledgementTimestamp(false, true);
		if (shouldRedirectAfterLogout()) {
			closeCurrentContext({ toLocation: ROUTE_HOME });
		} else {
			closeCurrentContext();
		}
		dispatch(signout({}));
	};

	const onShowHistory = () => {
		startRealityCheck();
		setShow(false);

		sendAppBridgeEvent(WEB_EVENTS.ON_SHOW_HISTORY_FROM_RC);

		router.push(transformPath('/account/transaction-history'));
		dispatch(realityCheckTimeReachedToggle(false));
	};

	// Button Configurations for all 4 buttons
	let buttonConfig = [];
	const resetContinue = {
		onClick: onContinue,
		dataAt: 'continue-button-reality-check',
		text: texts.MOBILE_REALITY_CHECK_CONTINUE_BUTTON
	};
	const disable = {
		onClick: onDisable,
		dataAt: 'disable-button-reality-check',
		text: texts.MOBILE_REALITY_CHECK_DISABLE_BUTTON,
		className: 'btn--disable-reality-check'
	};
	const transactionHistory = {
		onClick: onShowHistory,
		dataAt: 'transaction-history-button-reality-check',
		text: texts.MOBILE_REALITY_CHECK_HISTORY_BUTTON,
		className: 'btn--transaction-history-reality-check'
	};
	const logout = {
		onClick: onLogout,
		dataAt: 'logout-button-reality-check',
		text: texts.MOBILE_REALITY_CHECK_LOGOUT_BUTTON,
		className: 'btn--responsible-gaming-logout'
	};

	/**
	 * MGA(No account sites): Has only 3 option and transaction history is not available
	 * MGA, DGA, Pzbuk: Has all 4 buttons
	 * SGA: Has only 2 buttons 'remind me'/'continue playing' and 'logout' buttons
	 */
	if (license === LICENSE_MGA && !isRegularSite) {
		// adding 3 buttons to button config
		buttonConfig = [resetContinue, disable, logout];
	} else if ([LICENSE_MGA, LICENSE_DGA, LICENSE_PLGA, LICENSE_OGA, LICENSE_KSA].includes(license)) {
		// adding all 4 buttons to button config
		buttonConfig = [resetContinue, disable, transactionHistory, logout];
	} else if (license === LICENSE_SGA) {
		// adding 2 buttons to button config
		buttonConfig = [resetContinue, logout];
	}
	/**
	 * If the wrapper native app is in the native state (eg: Showing a native view) it should be switched to
	 * the webview in order to be able to show reality check to users in time.
	 */
	useEffect(() => {
		sendAppBridgeEvent(WEB_EVENTS.REALITY_CHECK_POPUP, [{ show: show || confirm.show }]);
	}, [show, confirm.show]);

	if (isLoadingTranslations) {
		return <div className="spinner" />;
	}

	if (confirm.show) {
		const { iconIdentifier, title, message, buttons } = confirm;
		return (
			<ConfirmMessage
				iconIdentifier={iconIdentifier}
				titleText={title}
				messageText={message}
				buttons={buttons}
				fromRealityCheck
			/>
		);
	}

	if (!show || isInGame) {
		return apiError ? (
			<BinaryQuestion
				shouldOpenBinary
				title={translations.API_ERROR_TITLE}
				text={translations.API_ERROR_DESCRIPTION}
				onYesClick={reloadPage}
				yesText={translations.BUTTON_OK}
				fixedPosition
			/>
		) : null;
	}
	return apiError ? (
		<BinaryQuestion
			shouldOpenBinary
			title={translations.API_ERROR_TITLE}
			text={translations.API_ERROR_DESCRIPTION}
			onYesClick={reloadPage}
			yesText={translations.BUTTON_OK}
			fixedPosition
		/>
	) : (
		<RealityCheckView
			texts={texts}
			isSwedishRegulatedFranchise={license === LICENSE_SGA}
			isMaltaRegulatedFranchise={license === LICENSE_MGA}
			buttonConfig={buttonConfig}
		/>
	);
};

export default RealityCheck;
